import React from "react"
import CMSData from "./cms-data"
import useCMSLocalization from "./cms-localization"
import Localization from "aldoo-ra/Localization"
import { usePageContext } from "vike-react/usePageContext"

// Static iterator for generating stable keys
let staticKeyIterator = 0

const CMSRender = ({ renderItem, render, preProcessItem, contentType, match, sort, itemsPerPage = 1000, page = 1, ...props }) => {
  const { data, loading, error, isClient } = CMSData({ contentType, match, sort, itemsPerPage, page, ...props })
  const { resolveLocalization } = useCMSLocalization()
  const { currentLanguage } = Localization()
  
  // Get SSR language from pageContext when available (server-side)
  let pageContext
  try {
    pageContext = usePageContext()
  } catch {
    // usePageContext not available (e.g., in tests or certain contexts)
    pageContext = null
  }
  
  // Use SSR language during server-side rendering, fallback to Localization hook
  const effectiveLanguage = (typeof window === 'undefined' && pageContext?.ssrLanguage) || currentLanguage || 'en'

  //provide default preProcessItem function
  //this will change the data prior to rendering
  //for example link alterations like {{platform}}/login
  if (!preProcessItem) {
    preProcessItem = (item) => item
  }

  // Helper function to flatten CMS data structure
  const flattenCMSData = (item) => {
    if (!item) return item
    
    // If item has nested content structure, flatten it
    if (item.content && typeof item.content === 'object') {
      // Create new item with flattened structure
      const flattenedItem = {
        ...item, // Keep top-level properties like _id, etc.
        ...item.content, // Flatten content properties to top level
      }
      
      // Remove the nested content property since we've flattened it
      delete flattenedItem.content
      
      return flattenedItem
    }
    
    return item
  }

  // Helper function to process data consistently
  const processData = (rawData, skipLocalization = false) => {
    return rawData.map(item => {
      try {
        // First, flatten the CMS data structure so components always see data.title instead of data.content.title
        const flattenedItem = flattenCMSData(item)
        
        // Skip localization if skipLocalization is true
        // This prevents double-localization in certain scenarios
        const localizedItem = (skipLocalization || !resolveLocalization) 
          ? flattenedItem 
          : resolveLocalization(flattenedItem)
        
        // Apply preprocessing
        return preProcessItem(localizedItem)
      } catch (error) {
        // If localization fails, fallback to original item
        console.warn('Localization failed:', error)
        return preProcessItem(flattenCMSData(item))
      }
    })
  }

  // Helper function to render data consistently
  const renderData = (processedData) => {
    if (render) return render(processedData)

    return processedData.map((item, index) => {
      // Generate a more stable key with multiple fallbacks
      const key = item._id || item.id || 
                 (item.content?.id) || 
                 (item.content?.name) || 
                 `${contentType}-${index}-${++staticKeyIterator}`
      
      
      return (
        <React.Fragment key={key}>
          {renderItem(item, index, processedData)}
        </React.Fragment>
      )
    })
  }

  // During SSR, show loading state - data will be fetched by CMSData
  if (!isClient) {
    return (
      <div aria-busy="true" role="status">
        {/* Loading... */}
      </div>
    )
  }

  // Client-side rendering
  if (loading) return <div>{/* Loading... */}</div>
  if (error) return <div>{error}</div>
  
  // Use the data from CMSData hook
  if (!data?.length) return null

  // Process the data with localization
  const processedData = processData(data, false)
  return renderData(processedData)
}

export default CMSRender
